import React from 'react'
import {
  Button,
  ButtonGroup
} from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { REQUEST_UPLOAD_START } from '../sagas'
import { runSaga } from 'store/sagaMiddleware'
import { call, put, take, spawn, actionChannel, delay } from 'redux-saga/effects'
import { action as apiAction } from 'api/Api'
import {
  add as addToast,
  set as setToast,
  remove as removeToast
} from 'store/redux/toasts'

const $ = window.$

function * delayedRemoveToast (uploadId) {
  yield delay(2000)

  yield put(removeToast({
    id: 'upload-' + uploadId
  }))
}

function createUploadObject (uploadId, file, url, size, cardId, onUpload) {
  return {
    uploadId,
    file,
    url,
    size,
    cardId,
    onUpload,
    uploadUrl: '/Upload/' + uploadId,
    currentByte: 0,
    chunkSize: 10240,
    minChunkSize: 32, // 32B
    maxChunkSize: 2 * 1024 * 1024, // 2MB, modify depending on limit
    lastLoadedByte: 0,
    targetDuration: 1000,
    chunkStartDate: null,
    chunkEndDate: null
  }
}

function adjustChunkSize (uploadObject) {
  const {
    chunkStartDate,
    chunkEndDate,
    currentByte,
    lastLoadedByte,
    chunkSize,
    minChunkSize,
    maxChunkSize,
    targetDuration
  } = uploadObject

  const duration = chunkEndDate - chunkStartDate
  const loadedSize = lastLoadedByte - currentByte
  uploadObject.chunkSize = Math.floor(loadedSize * (targetDuration / duration))
  if (chunkSize < minChunkSize) uploadObject.chunkSize = minChunkSize
  if (chunkSize > maxChunkSize) uploadObject.chunkSize = maxChunkSize
}

function readChunk (uploadObject) {
  const {
    currentByte,
    chunkSize,
    size,
    file
  } = uploadObject

  let end = currentByte + chunkSize
  end = end > size ? size : end

  uploadObject.lastLoadedByte = end

  return new Promise((resolve, reject) => {
    const blob = file.slice(currentByte, end)
    const reader = new FileReader()
    reader.onloadend = function (event) {
      if (event.target.readyState === FileReader.DONE) {
        resolve(event.target.result)
      }
    }
    reader.readAsArrayBuffer(blob)
  })
}

function * loadChunk (uploadObject) {
  const chunk = yield call(readChunk, uploadObject)

  yield call(sendChunk, chunk, uploadObject)

  yield chunkSent(uploadObject)
}

function sendChunk (chunk, uploadObject) {
  const {
    uploadUrl
  } = uploadObject

  uploadObject.chunkStartDate = new Date()

  return new Promise((resolve, reject) => {
    $.ajax({
      url: uploadUrl,
      data: chunk,
      type: 'POST',
      processData: false,
      cache: false
    }).done(function () {
      resolve()
    }).fail(function (jqXHR, message) {
      reject()
    })
  })
}

function * chunkSent (uploadObject) {
  const {
    lastLoadedByte,
    currentByte,
    size,
    uploadId
  } = uploadObject

  uploadObject.chunkEndDate = new Date()

  adjustChunkSize(uploadObject)

  uploadObject.currentByte = lastLoadedByte

  const progress = Math.floor(currentByte / size * 100) || 0

  yield put(setToast({
    id: 'upload-' + uploadId,
    progress
  }))

  if (currentByte < size) {
    yield loadChunk(uploadObject)
  } else {
    yield finalize(uploadObject)
  }
}

function * finalize (uploadObject) {
  const {
    uploadId,
    onUpload,
    cardId,
  } = uploadObject

  let endUploadMethod = 'EndUploadSingle'
  if (cardId) {
    endUploadMethod = 'EndUploadSingleToCard'
  }

  const { DocumentsSourceId, Url, ElementId } = yield call(apiAction, endUploadMethod, {
    id: uploadId
  })

  yield put(setToast({
    id: 'upload-' + uploadId,
    type: 'success',
    progress: 100
  }))

  yield spawn(delayedRemoveToast, uploadId)

  if (onUpload) {
    yield onUpload({
      url: Url,
      documentSourceId: DocumentsSourceId,
      elementId: ElementId,
    })
  }
}

function * uploadHandler (action) {
  let { file, name, path = '/', cardId, documentSourceId, boardId, overwrite, uniqueNameCounter, onUpload } = action.payload

  let fileName = name || file.name

  const pathLength = path.length
  if (!pathLength || path.charAt(pathLength - 1) !== '/') {
    path += '/'
  }

  if (uniqueNameCounter) {
    const index = fileName.lastIndexOf('.')
    const hasExt = index >= 0

    const name =
      hasExt
        ? fileName.substr(0, index)
        : fileName

    const ext =
      hasExt
        ? fileName.substr(index + 1)
        : ''

    fileName = name + ' (' + uniqueNameCounter + ')'
    if (hasExt) fileName += '.' + ext
  }

  const url = path + fileName
  const size = file.size

  const args = {
    path: url,
    size
  }

  if (overwrite === true) {
    args.overwrite = overwrite
  }

  let startUploadMethod = 'StartUploadSingle'

  if (cardId) {
    startUploadMethod = 'StartUploadSingleToCard'
    args.cardId = cardId
  } else if (documentSourceId) {
    args.documentsSourceId = documentSourceId
  } else {
    args.boardId = boardId
  }

  try {
    const { Value: uploadId } = yield call(apiAction, startUploadMethod, args)

    yield put(addToast({
      id: 'upload-' + uploadId,
      message: (
        <FormattedMessage
          id='UPLOAD_FILE_PROGRESS_RUNNING'
          values={{
            fileName: <b>{fileName}</b>
          }}
        />
      ),
      progress: 0
    }))

    try {
      const uploadObject = createUploadObject(uploadId, file, url, size, cardId, onUpload)

      yield loadChunk(uploadObject)
    } catch (error) {
      yield put(removeToast({
        id: 'upload-' + uploadId
      }))

      throw error
    }
  } catch (error) {
    if (error && error.code === 509) {
      const id = 'uploadExists-' + Date.now()

      if (overwrite === false) {
        yield put({
          type: REQUEST_UPLOAD_START,
          payload: {
            ...action.payload,
            uniqueNameCounter: uniqueNameCounter + 1
          }
        })
      } else {
        yield put(addToast({
          id,
          type: 'warning',
          message: (
            <div>
              <div>
                <FormattedMessage
                  id='UPLOAD_FILE_EXISTS'
                  values={{
                    fileName: <b>{fileName}</b>
                  }}
                />
              </div>
              <div>
                <ButtonGroup style={{ marginTop: 5 }}>
                  <Button
                    bsStyle='primary'
                    bsSize='small'
                    onClick={() => runSaga(function * () {
                      yield put(removeToast({
                        id
                      }))

                      yield put({
                        type: REQUEST_UPLOAD_START,
                        payload: {
                          ...action.payload,
                          overwrite: true
                        }
                      })
                    })}>
                    <FormattedMessage id='UPLOAD_OVERWRITE' />
                  </Button>
                  <Button
                    bsStyle='primary'
                    bsSize='small'
                    onClick={() => runSaga(function * () {
                      yield put(removeToast({
                        id
                      }))

                      yield put({
                        type: REQUEST_UPLOAD_START,
                        payload: {
                          ...action.payload,
                          overwrite: false,
                          uniqueNameCounter: 1
                        }
                      })
                    })}>
                    <FormattedMessage id='UPLOAD_DONT_OVERWRITE' />
                  </Button>
                  <Button
                    bsSize='small'
                    onClick={() => runSaga(function * () {
                      yield put(removeToast({
                        id
                      }))
                    })}>
                    <FormattedMessage id='CANCEL' />
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          )
        }))
      }
    } else if (error && error.code === 523) {
      const id = 'uploadExists-' + Date.now()

      let validFileName = error.exception.SuggestedPath

      const index = validFileName.lastIndexOf('/')
      if (index !== -1) {
        validFileName = validFileName.substr(index + 1)
      }

      yield put(addToast({
        id,
        type: 'warning',
        message: (
          <div>
            <div>
              <FormattedMessage
                id='UPLOAD_INVALID_NAME'
                values={{
                  invalidFileName: <b>{fileName}</b>,
                  validFileName: <b>{validFileName}</b>
                }}
              />
            </div>
            <div>
              <ButtonGroup style={{ marginTop: 5 }}>
                <Button
                  bsStyle='primary'
                  bsSize='small'
                  onClick={() => runSaga(function * () {
                    yield put(removeToast({
                      id
                    }))

                    yield put({
                      type: REQUEST_UPLOAD_START,
                      payload: {
                        ...action.payload,
                        name: validFileName
                      }
                    })
                  })}>
                  <FormattedMessage id='UPLOAD' />
                </Button>
                <Button
                  bsSize='small'
                  onClick={() => runSaga(function * () {
                    yield put(removeToast({
                      id
                    }))
                  })}>
                  <FormattedMessage id='CANCEL' />
                </Button>
              </ButtonGroup>
            </div>
          </div>
        )
      }))
    } else {
      const id = 'uploadError-' + Date.now()

      yield put(addToast({
        id,
        type: 'warning',
        message: (
          <div>
            <div>
              <FormattedMessage
                id='UPLOAD_FILE_ERROR'
                values={{
                  fileName: <b>{fileName}</b>
                }}
              />
            </div>
            <div>
              <ButtonGroup style={{ marginTop: 5 }}>
                <Button
                  bsStyle='primary'
                  bsSize='small'
                  onClick={() => runSaga(function * () {
                    yield put(removeToast({
                      id
                    }))

                    yield put({
                      type: REQUEST_UPLOAD_START,
                      payload: {
                        ...action.payload
                      }
                    })
                  })}>
                  <FormattedMessage id='UPLOAD_RETRY' />
                </Button>
                <Button
                  bsSize='small'
                  onClick={() => runSaga(function * () {
                    yield put(removeToast({
                      id
                    }))
                  })}>
                  <FormattedMessage id='CANCEL' />
                </Button>
              </ButtonGroup>
            </div>
          </div>
        )
      }))
    }
  }
}

export default function * uploadListener () {
  console.log('uploadListener has been started')

  // 1- Create a channel for request actions
  const requestChan = yield actionChannel(REQUEST_UPLOAD_START)
  while (true) {
    // 2- take from the channel
    const action = yield take(requestChan)
    // 3- Note that we're using a blocking call
    yield call(uploadHandler, action)
  }
}
